/**
 * Composer request options that ride on every chat turn.
 *
 * These are the Word add-in's equivalent of the webapp's composer state
 * (deep-analysis / web-search toggles, the connections picker, attachments):
 * the shared `ChatMessageContext` the backend requires on every stream/message
 * request. Both the initial stream body and follow-up messages derive them from
 * here, so wiring the corresponding UI later only needs to update this store —
 * no request-building code changes.
 *
 * Uploaded files ride on the user message's metadata instead, so a retry
 * resends them and a follow-up never does.
 */ import { extensionOf, uploadFile } from "@Api/uploadFile";
import { PreviewableDocumentFormatTypes, PreviewableImageFileTypes, PreviewableOfficeFileTypes, SupportedEmailTypes } from "@juristic/types/generic/file";
import { create } from "zustand";
const APPLY_MODE_KEY = "wordApplyMode";
function readApplyMode() {
    try {
        return localStorage.getItem(APPLY_MODE_KEY) === "auto" ? "auto" : "review";
    } catch  {
        return "review";
    }
}
function storeApplyMode(mode) {
    try {
        localStorage.setItem(APPLY_MODE_KEY, mode);
    } catch  {
    // Without storage the choice lasts for this page load.
    }
}
export const ACCEPTED_ATTACHMENT_TYPES = [
    ...Object.values(PreviewableDocumentFormatTypes),
    ...Object.values(PreviewableOfficeFileTypes),
    ...Object.values(SupportedEmailTypes),
    ...Object.values(PreviewableImageFileTypes)
].map(String);
const ACCEPTED_TYPES_SET = new Set(ACCEPTED_ATTACHMENT_TYPES);
export const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;
let nextAttachmentId = 0;
export const useChatComposerStore = create((set, get)=>{
    const patch = (id, change)=>set(({ attachments })=>({
                attachments: attachments.map((attachment)=>attachment.id === id ? {
                        ...attachment,
                        ...change
                    } : attachment)
            }));
    const start = (attachment)=>uploadFile(attachment.file).then((upload)=>patch(attachment.id, {
                status: "done",
                upload
            }), (error)=>{
            console.warn("[chat] upload failed:", error);
            patch(attachment.id, {
                status: "failed"
            });
        });
    return {
        shouldMakeDeepAnalysis: false,
        shouldAllowWebsearch: false,
        connections: [],
        attachments: [],
        wordApplyMode: readApplyMode(),
        setWordApplyMode: (wordApplyMode)=>{
            storeApplyMode(wordApplyMode);
            set({
                wordApplyMode
            });
        },
        setShouldMakeDeepAnalysis: (shouldMakeDeepAnalysis)=>set({
                shouldMakeDeepAnalysis
            }),
        setShouldAllowWebsearch: (shouldAllowWebsearch)=>set({
                shouldAllowWebsearch
            }),
        setConnections: (connections)=>set({
                connections
            }),
        setAttachments: (attachments)=>set({
                attachments
            }),
        addFiles: (files)=>{
            const accepted = files.filter((file)=>ACCEPTED_TYPES_SET.has(extensionOf(file.name)) && file.size <= MAX_ATTACHMENT_BYTES);
            const room = MAX_ATTACHMENTS - get().attachments.length;
            const added = accepted.slice(0, Math.max(room, 0)).map((file)=>({
                    id: `attachment-${nextAttachmentId += 1}`,
                    file,
                    status: "uploading"
                }));
            set(({ attachments })=>({
                    attachments: [
                        ...attachments,
                        ...added
                    ]
                }));
            added.forEach(start);
            if (accepted.length > added.length) return "tooMany";
            if (files.some((file)=>!ACCEPTED_TYPES_SET.has(extensionOf(file.name)))) return "unsupported";
            return accepted.length < files.length ? "tooLarge" : null;
        },
        retryAttachment: (id)=>{
            const attachment = get().attachments.find((item)=>item.id === id);
            if (!attachment) return;
            patch(id, {
                status: "uploading"
            });
            start(attachment);
        },
        removeAttachment: (id)=>set(({ attachments })=>({
                    attachments: attachments.filter((item)=>item.id !== id)
                }))
    };
});
/**
 * The message-context slice sent on every turn. Read via `getState()` at send
 * time (not render time) so requests always carry the latest composer values.
 */ export function getMessageContext() {
    const { shouldMakeDeepAnalysis, shouldAllowWebsearch, connections } = useChatComposerStore.getState();
    return {
        shouldMakeDeepAnalysis,
        shouldAllowWebsearch,
        connections: connections.map(({ id, type })=>({
                id,
                type
            })),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
}
